import moment from 'moment';
import i18n from '@/locale';

const palettes = {
  palette2: ['#3F51B5', '#03A9F4', '#4CAF50', '#F9CE1D', '#FF9800'],
};

export class ApexOptionHelper{

  static genOptionsForSparkline(){
    return {
      stroke: {curve: 'straight'},
      fill: {opacity: 1},
      tooltip: {
        x: {format: 'yyyy-MM-dd'},
      },
      xaxis: {type: 'datetime'},
      yaxis: {
        show: false,
        // min: 0,
        labels: {
          formatter: (val, index) => g_round_off(val),
        },
      },
    };
  }

  static genOptionsForTitle(){
    return {
      title: {
        offsetX: 4,
        offsetY: 4,
        style: {
          fontSize: '24px',
          cssClass: 'apexcharts-yaxis-title',
        },
      },
      subtitle: {
        offsetX: 4,
        offsetY: 36,
        style: {
          fontSize: '14px',
          cssClass: 'apexcharts-yaxis-title',
        },
      },
    };
  }

  static assignColors(series, paletteColors = palettes.palette2){
    for(let idx = 0; idx < series.length; idx++)
      series[idx].color = paletteColors[idx % paletteColors.length];
  }
}

export class ApexHandlerBase{

  constructor(option_init){
    this._option_init = _.cloneDeep(_.merge(
      {
        chart: {
          sparkline: {enabled: true},
        },
        theme: {mode: 'dark'},
      },
      option_init,
    ));
    this.clearStaging();
    this.clearCommitted();
  }

  clearStaging(){
    this._option_staging = {};
    this._series = [];
    return this;
  }

  clearCommitted(){
    this._option_committed = _.cloneDeep(this._option_init);
    this._series = [];
    return this;
  }

  mergeOption(p_val){
    _.merge(this._option_staging, p_val);
    return this;
  }

  commitOption(){
    this._option_committed = _.merge(_.cloneDeep(this._option_init), _.cloneDeep(this._option_staging));
    return this;
  }

  clearData(){
    return this.clearCommitted();
  }
}

export class ApexHandlerSparkline extends ApexHandlerBase{

  constructor(option_init){
    super(_.merge({
      ...ApexOptionHelper.genOptionsForSparkline(),
      ...ApexOptionHelper.genOptionsForTitle(),
      chart: {type: 'area'},
    }, option_init));
  }

  buildOption(aqt_termly, x_col, y_col, {
    is_avg = false,
    rds_total,
    total_col,
    subtitle_prefix = '',
    number_format,
  } = {}){
    const y_value_sum = g_aq.agg(aqt_termly, g_aq.op.sum(y_col));
    const y_value_avg = g_aq.agg(aqt_termly, g_aq.op.average(y_col));
    const subtitle_field = y_col + (is_avg ? '|avg' : '|total');

    total_col ??= y_col;

    let total_value = undefined;
    if(rds_total) total_value = rds_total[total_col];
    else if(is_avg) total_value = y_value_avg;
    else if(!is_avg) total_value = y_value_sum;

    const anno_value = rds_total ? total_value : y_value_avg;

    this.clearStaging().mergeOption({
      title: {text: g_number_2_text(total_value, number_format)},
      subtitle: {text: subtitle_prefix + i18n.tfd(subtitle_field)},
      // labels: aqt_termly.array(x_col),
      tooltip: {
        y: {
          formatter: (value) => g_number_2_text(value, number_format),
        },
      },
      annotations: {
        yaxis: [
          {
            y: anno_value,
            label: {
              text: i18n.tfd('|avg') + `: ${g_number_2_text(anno_value, number_format)}`,
              textAnchor: 'start',
              position: 'left',
              borderColor: 'rgba(0, 0, 0, 0)',
              style: {
                background: 'rgba(0, 0, 0, 0)',
                fontSize: '13px',
                fontWeight: 600,
              },
            },
          },
        ],
      },
    });

    this.commitOption();
    this._series = [{
      name: i18n.tfd(y_col),
      data: aqt_termly.select({[x_col]: 'x', [y_col]: 'y'}).objects(),
    }];
    return this;
  }
}

export class ApexHandlerMixed extends ApexHandlerBase{

  constructor(option_init){
    super(_.merge({
      ...ApexOptionHelper.genOptionsForSparkline(),
      ...ApexOptionHelper.genOptionsForTitle(),
      chart: {type: 'area'},
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        offsetX: 80,
        offsetY: -80,
      },
    }, option_init));
  }

  buildOption(aqt_termly, x_col, columns){

    aqt_termly.orderby(x_col);

    const fill_types = columns.map(em => {
      if(em.type == 'area')
        return 'gradient';
      return 'soild';
    });

    this.clearStaging().mergeOption({
      title: {text: '.'},
      subtitle: {text: '.'},
      fill: {
        type: fill_types,
      },
    });
    this.commitOption();

    this._series = columns.map(em => {
      return {
        type: em.type,
        color: em?.color,
        name: i18n.tfd(em.field),
        data: aqt_termly.select({[x_col]: 'x', [em.field]: 'y'}).objects(),
      };
    });

    // ApexOptionHelper.assignColors(this._series);
    return this;
  }
}

export class ApexHandlerCandlestick extends ApexHandlerBase{

  constructor(option_init){
    super(_.merge({
      chart: {
        type: 'candlestick',
        sparkline: {enabled: false},
        toolbar: {show: false},
        zoom: {enabled: false},
      },
      ...ApexOptionHelper.genOptionsForTitle(),
      grid: {show: false},
      tooltip: {
        // enabled: false,
        custom: function({series, seriesIndex, dataPointIndex, w}) {
          const w_series = w.config?.series[seriesIndex];
          const d_series = series[seriesIndex];
          const curr_value = d_series[dataPointIndex];
          const prev_value = (0 == dataPointIndex ? 0 : d_series[dataPointIndex - 1]);
          const diff_value = curr_value - prev_value;
          const ret_div = document.createElement('div');
          ret_div.style.padding = '10px';

          [
            [i18n.tfd(w_series?.name), g_number_2_text(diff_value)],
            ['From', g_number_2_text(prev_value)],
            ['To', g_number_2_text(curr_value)],
          ].forEach(em => {
            let child_div = document.createElement('div');
            // child_div.textContent = `${em[0]}: ${em[1]}`;

            child_div.style.display = 'flex';
            child_div.style.justifyContent = 'space-between';
            child_div.style.width = '100%';

            let title = document.createElement('span');
            title.textContent = `${em[0]}:`;
            title.style['padding-right'] = '10px';
            child_div.appendChild(title);

            let content = document.createElement('span');
            content.textContent = em[1];
            child_div.appendChild(content);

            ret_div.appendChild(child_div);
          });
          return ret_div;
        },

        x: {format: 'yyyy-MM-dd'},
        // y: {
        //   formatter: function(value, { series, seriesIndex, dataPointIndex, w }) {
        //     // console.log(value)
        //     return value;
        //   },
        //   title: {
        //     formatter: (seriesName) => {
        //       console.log(seriesName)
        //       return seriesName;
        //     },
        //   },
        // },

      },
      xaxis: {
        type: 'datetime',
        labels: {
          show: false,
          // format: 'yyyy-MM-dd',
          datetimeFormatter: {
            year: 'yyyy',
            month: "MMM",
            day: 'MM/dd',
            // hour: 'HH:mm',
            // minute: 'HH:mm:ss',
            // second: 'HH:mm:ss',
          },
        },
        axisBorder: {show: false},
        axisTicks: {show: false},
      },
      yaxis: {show: false},
    }, option_init));
  }

  buildOption(aqt_termly, x_col, metric_name){

    this.clearStaging().mergeOption({
      subtitle: {text: i18n.tfd(metric_name)},
    });
    this.commitOption();

    const rds_data = aqt_termly
    .params({metric_name})
    .orderby(x_col)
    .derive({run_ttl_curr: g_aq.rolling((d, $) => g_aq.op.sum(d[$.metric_name]))})
    .derive({run_ttl_prev: g_aq.op.lag('run_ttl_curr', 1, 0)})
    .derive({y: d => [d.run_ttl_prev, d.run_ttl_prev, d.run_ttl_curr, d.run_ttl_curr]})
    .select({[x_col]: 'x', y: 'y'})
    .objects()

    this._series = [{
      name: metric_name,
      data: rds_data,
    }];
    return this;
  }
}

export class ApexHandlerDonut extends ApexHandlerBase{

  constructor(){super({
    chart: {
      type: 'donut',
      sparkline: {enabled: false},
    },
    legend: {
      floating: true,
      position: 'left',
    },
    tooltip: {enabled: false},
    plotOptions: {
      pie: {
        donut: {
          size: '66.7%',
          labels: {
            show: true,
            total: {
              show: true,
              // showAlways: true,
              label: 'Total',
              fontSize: '20px',
              formatter: (w) => w.globals.seriesTotals.reduce((a, b) => {return a + b}, 0),
            },
          },
        },

      },
    },
  })}

  buildOption(p_aqt, x_col, y_col){
    this.clearStaging().mergeOption({
      // title: {text: g_number_2_text(title_value, number_format)},
      // subtitle: {text: subtitle_prefix + i18n.tfd(subtitle_field)},

      // title: {text: 'ABASDF'},
      // subtitle: {text: 'ABASDF'},
      labels: p_aqt.array(x_col),
    });

    this.commitOption();
    // this._series = [{
    //   name: i18n.tfd(y_col),
    //   data: p_aqt.array(y_col),
    // }];
    this._series = p_aqt.array(y_col);
    return this;
  }
}

export class ApexHandlerFunnel extends ApexHandlerBase{

  constructor(){super({
    chart: {type: 'bar'},
    plotOptions: {
      bar: {
        horizontal: true,
        isFunnel: true,
      },
    },
    tooltip: {
      y: {
        formatter: (value, {series, seriesIndex, dataPointIndex, w}) => {
          const rate = series[0][dataPointIndex] / series[0][0];
          return g_number_2_text(rate, {is_percent: true, fraction_digits: 2});
        },
      },
    },
    dataLabels: {
      enabled: true,
      textAnchor: 'end',
      dropShadow: {enabled: true},
      formatter: (val, opt) => {
        return opt.w.globals.labels[opt.dataPointIndex] + ':  ' + g_number_2_text(val);
      },
    },
  })}

  buildOption(data_row, metric_name, columns){
    this.clearStaging();

    const data = columns.map((em_i) => {
      return {
        x: em_i.title,
        y: data_row[em_i.field],
        // fillColor: '#2E93fA',
      };
    });
    this._series = [{data, name: metric_name}];
    return this;
  }
}

export class ApexHandlerLine extends ApexHandlerBase{

  constructor(){super({
    // chart: {type: 'line'},
    chart: {
      type: 'line',
      // sparkline: {enabled: false},
      dropShadow: {
        enabled: true,
        top: 3,
        left: 2,
        blur: 4,
        opacity: 1,
      },
    },
    stroke: {
      curve: 'straight',
      width: 1.5,
    },

    colors: ["#C7f464", "#81D4FA", "#546E7A"],
    // title: {
    //   text: 'Media',
    //   align: 'left',
    //   offsetY: 25,
    //   offsetX: 20,
    // },
    // subtitle: {
    //   text: 'Statistics',
    //   offsetY: 55,
    //   offsetX: 20,
    // },
    markers: {
      size: 3,
      strokeWidth: 0,
      hover: {size: 6},
    },
    // grid: {
    //   show: true,
    //   padding: {
    //     bottom: 0
    //   }
    // },
    // xaxis: {
    //   tooltip: {
    //     enabled: false
    //   }
    // },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      // offsetY: -10,
    },
  })}

  buildOption(data_row, metric_name, columns){
    // const data = columns.map((em_i) => {
    //   return {
    //     x: em_i.title,
    //     y: data_row[em_i.field],
    //     // fillColor: '#2E93fA',
    //   };
    // });
    // this._series = [{data, name: metric_name}];

    this.clearStaging().mergeOption({
      // title: {text: g_number_2_text(title_value, number_format)},
      // subtitle: {text: subtitle_prefix + i18n.tfd(subtitle_field)},
      labels: ['01/15/2002', '01/16/2002', '01/17/2002', '01/18/2002', '01/19/2002', '01/20/2002'],
    });
    this.commitOption();

    this._series = [{
        name: "Music",
        data: [20, 15, 26, 20, 33, 27]
      },
      {
        name: "Photos",
        data: [30, 33, 21, 42, 19, 32]
      },
      {
        name: "Files",
        data: [0, 39, 52, 11, 29, 43]
      }
    ];
    return this;
  }
}

export class ApexHandlerRangeBar extends ApexHandlerBase{

  constructor(option_init){
    super(_.merge({
      chart: {
        type: 'rangeBar',
        sparkline: {enabled: false},
        toolbar: {show: false},
        zoom: {enabled: false},
        // height: 420,
      },
      // ...ApexOptionHelper.genOptionsForTitle(),
      grid: {show: false},
      tooltip: {
        // enabled: false,
        x: {format: 'yyyy-MM-dd'},
      },
      xaxis: {
        type: 'datetime',
        labels: {
          // show: false,
          format: 'yyyy-MM-dd',
          // datetimeFormatter: {
          //   year: 'yyyy',
          //   month: "MMM",
          //   day: 'MM/dd',
          // },
        },
        axisBorder: {show: false},
        axisTicks: {show: false},
      },
      // yaxis: {show: false},
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
        },
      },
      theme: {
        palette: 'palette2',
      },
    }, option_init));
  }

  buildOption(
    p_aqt,
    p_rpt_date_start,
    p_rpt_date_end,
    x_col,
    col_start,
    col_end
  ){
    this.clearStaging().mergeOption({
      xaxis: {
        min: new Date(p_rpt_date_start).getTime(),
        max: new Date(p_rpt_date_end).getTime(),
        range: new Date(p_rpt_date_end) - new Date(p_rpt_date_start),
      },
    });
    this.commitOption();

    const rds_data = p_aqt
    // .params({metric_name})
    .derive({y: g_aq.escape(d => [
      new Date(d.promo_date_start).getTime(),
      new Date(moment(d.promo_date_end).add(1, 'day').format('YYYY-MM-DD')).getTime()
    ])})
    .select({
      [x_col]: 'x',
      y: 'y',
    })
    .objects()

    this._series = [{
      // name: 'metric_name',
      data: rds_data,
    }];
    return this;
  }

  getHeight(){
    let ret_height = this._series[0]?.data.length;
    if(ret_height < 8)
      ret_height = 8;
    return `height: ${ret_height * 30}px;`;
  }
}
